import axios from 'axios';
import { readPref, writePref } from '@/lib/prefs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Sessions are carried by an httpOnly cookie set by the backend on login/signup.
// The JWT is never exposed to page scripts (nothing is persisted in localStorage).
const client = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' },
  timeout: 15000,
  withCredentials: true,
});

// ---------------- Marketplace tenancy: LXRA owner "view as" a studio ----------------
// Photographers are always scoped to their own studio server-side; only the LXRA
// owner can switch tenants. The choice is a non-sensitive UI preference.
const VIEW_AS_PREF_KEY = 'lxra.admin.viewAs';
let activeStudioId = readPref(VIEW_AS_PREF_KEY, '') || null;
const viewAsListeners = new Set();
export const getActiveStudioId = () => activeStudioId;
export const setActiveStudioId = (id) => {
  activeStudioId = id || null;
  writePref(VIEW_AS_PREF_KEY, activeStudioId || '');
  viewAsListeners.forEach((fn) => fn(activeStudioId));
};
export const onActiveStudioChange = (fn) => { viewAsListeners.add(fn); return () => viewAsListeners.delete(fn); };
client.interceptors.request.use((config) => {
  if (activeStudioId) config.headers['X-Studio-Id'] = activeStudioId;
  return config;
});

export const getSiteConfig = () => client.get('/site-config').then((r) => r.data);
export const getWeather = () => client.get('/weather').then((r) => r.data);
export const getGalleries = () => client.get('/galleries').then((r) => r.data);
export const getGalleryBySlug = (slug) => client.get(`/galleries/${slug}`).then((r) => r.data);
export const getClientOwnedGallery = (slug) => client.get(`/client/galleries/${slug}`).then((r) => r.data);
export const unlockGallery = (slug, accessCode) =>
  client.post(`/galleries/${slug}/unlock`, { access_code: accessCode }).then((r) => r.data);
export const getServices = () => client.get('/services').then((r) => r.data);
export const getServiceBySlug = (slug) => client.get(`/services/${slug}`).then((r) => r.data);
export const submitContact = (payload) => client.post('/contact', payload).then((r) => r.data);
export const getJournalPosts = () => client.get('/journal').then((r) => r.data);
export const getJournalPostBySlug = (slug) => client.get(`/journal/${slug}`).then((r) => r.data);

// ---------------- Gear & Photography Library ----------------
export const getGearLibrary = (params = {}) => client.get('/gear', { params }).then((r) => r.data);
export const getGearLibraryBySlug = (slug) => client.get(`/gear/${slug}`).then((r) => r.data);
export const matchGearLibrary = (name) => client.get('/gear/match/lookup', { params: { name } }).then((r) => r.data);
export const adminListGearLibrary = () => client.get('/admin/gear').then((r) => r.data);
export const adminCreateGearLibrary = (payload) => client.post('/admin/gear', payload).then((r) => r.data);
export const adminGenerateGearLibrary = (payload) => client.post('/admin/gear/generate', payload).then((r) => r.data);
export const adminUpdateGearLibrary = (id, payload) => client.patch(`/admin/gear/${id}`, payload).then((r) => r.data);
export const adminPublishGearLibrary = (id) => client.post(`/admin/gear/${id}/publish`).then((r) => r.data);
export const adminUnpublishGearLibrary = (id) => client.post(`/admin/gear/${id}/unpublish`).then((r) => r.data);
export const adminDeleteGearLibrary = (id) => client.delete(`/admin/gear/${id}`).then((r) => r.data);
export const subscribeNewsletter = (email) =>
  client.post('/newsletter/subscribe', { email }).then((r) => r.data);

// ---------------- Auth ----------------
export const adminLogin = (email, password) =>
  client.post('/auth/admin/login', { email, password }).then((r) => r.data);
export const clientLogin = (email, password) =>
  client.post('/auth/client/login', { email, password }).then((r) => r.data);
export const clientSignup = (email, password, name) =>
  client.post('/auth/client/signup', { email, password, name }).then((r) => r.data);
export const photographerSignup = ({ inviteCode, email, password, name, studioName, homeBase, styles }) =>
  client.post('/auth/photographer/signup', {
    invite_code: inviteCode || null, email, password, name,
    studio_name: studioName || '', home_base: homeBase || '', styles: styles || [],
  }).then((r) => r.data);
export const getMe = () => client.get('/auth/me').then((r) => r.data);
export const logoutSession = () => client.post('/auth/logout').then((r) => r.data);
export const changeAdminPassword = (currentPassword, newPassword) =>
  client
    .post('/auth/admin/change-password', { current_password: currentPassword, new_password: newPassword })
    .then((r) => r.data);
export const changeClientPassword = (currentPassword, newPassword) =>
  client
    .post('/auth/client/change-password', { current_password: currentPassword, new_password: newPassword })
    .then((r) => r.data);

// ---------------- Two-factor authentication (TOTP) ----------------
export const getMfaStatus = () => client.get('/auth/mfa/status').then((r) => r.data);
export const setupMfa = () => client.post('/auth/mfa/setup').then((r) => r.data);
export const enableMfa = (code) => client.post('/auth/mfa/enable', { code }).then((r) => r.data);
export const disableMfa = (password, code) => client.post('/auth/mfa/disable', { password, code }).then((r) => r.data);
export const regenerateMfaBackupCodes = (password) =>
  client.post('/auth/mfa/backup-codes/regenerate', { password }).then((r) => r.data);
export const submitMfaChallenge = (challengeToken, code) =>
  client.post('/auth/mfa/challenge', { challenge_token: challengeToken, code }).then((r) => r.data);

// ---------------- Admin CMS ----------------
export const getAdminStats = () => client.get('/admin/stats').then((r) => r.data);
export const listContactMessages = () => client.get('/contact').then((r) => r.data);
export const listNewsletterSubscribers = () => client.get('/admin/newsletter').then((r) => r.data);

export const adminListAllGalleries = () => client.get('/admin/galleries').then((r) => r.data);
export const adminListGalleryImages = (slug) => client.get(`/admin/galleries/${slug}/images`).then((r) => r.data);
export const listClients = () => client.get('/admin/clients').then((r) => r.data);
export const createClient = (payload) => client.post('/admin/clients', payload).then((r) => r.data);
export const updateClient = (id, payload) => client.patch(`/admin/clients/${id}`, payload).then((r) => r.data);
export const deleteClient = (id) => client.delete(`/admin/clients/${id}`).then((r) => r.data);

export const adminListTeam = () => client.get('/admin/team').then((r) => r.data);
export const adminListStaffInvites = () => client.get('/admin/staff-invites').then((r) => r.data);
export const adminCreateStaffInvite = (expiresInHours, email, kind = 'team') =>
  client.post('/admin/staff-invites', { expires_in_hours: expiresInHours, email: email || null, kind }).then((r) => r.data);
export const adminRevokeStaffInvite = (id) => client.delete(`/admin/staff-invites/${id}`).then((r) => r.data);

export const adminCreateGallery = (payload) => client.post('/admin/galleries', payload).then((r) => r.data);
export const adminUpdateGallery = (slug, payload) => client.put(`/admin/galleries/${slug}`, payload).then((r) => r.data);
export const adminDeleteGallery = (slug) => client.delete(`/admin/galleries/${slug}`).then((r) => r.data);
export const adminAddGalleryImage = (slug, payload) =>
  client.post(`/admin/galleries/${slug}/images`, payload).then((r) => r.data);
export const adminDeleteGalleryImage = (slug, imageId) =>
  client.delete(`/admin/galleries/${slug}/images/${imageId}`).then((r) => r.data);

export const adminListJournalPosts = () => client.get('/admin/journal').then((r) => r.data);
export const adminCreateJournalPost = (payload) => client.post('/admin/journal', payload).then((r) => r.data);
export const adminSuggestJournalCover = (payload) => client.post('/admin/journal/suggest-cover', payload, { timeout: 45000 }).then((r) => r.data);
export const adminUpdateJournalPost = (slug, payload) => client.put(`/admin/journal/${slug}`, payload).then((r) => r.data);
export const adminPublishJournalPost = (slug) => client.post(`/admin/journal/${slug}/publish`).then((r) => r.data);
export const adminUnpublishJournalPost = (slug) => client.post(`/admin/journal/${slug}/unpublish`).then((r) => r.data);
export const adminDeleteJournalPost = (slug) => client.delete(`/admin/journal/${slug}`).then((r) => r.data);

// ---------------- Autonomous Blog Writer ----------------
export const adminGetBlogAutomationSettings = () => client.get('/admin/blog-automation/settings').then((r) => r.data);
export const adminUpdateBlogAutomationSettings = (payload) => client.patch('/admin/blog-automation/settings', payload).then((r) => r.data);
export const adminGenerateBlogDraftNow = () => client.post('/admin/blog-automation/generate-now', {}, { timeout: 60000 }).then((r) => r.data);

// ---------------- LXRA Intelligence (public) ----------------
export const getAiHealth = () => client.get('/ai/health').then((r) => r.data);
export const adminRunShareReminders = () => client.post('/admin/share-reminders/run').then((r) => r.data);
export const adminGetShareReminderSettings = () => client.get('/admin/share-reminders/settings').then((r) => r.data);
export const adminUpdateShareReminderSettings = (leadDays) =>
  client.patch('/admin/share-reminders/settings', { lead_days: leadDays }).then((r) => r.data);
export const adminGetAIUsage = (month) => client.get('/admin/ai/usage', { params: month ? { month } : {} }).then((r) => r.data);
// ---------------- Bring-Your-Own-Key (OpenAI / Anthropic / Gemini) ----------------
export const adminGetBYOKStatus = () => client.get('/admin/ai/byok').then((r) => r.data);
export const adminSaveBYOKKey = (provider, apiKey, model, imageModel) =>
  client.put(`/admin/ai/byok/${provider}`, { api_key: apiKey, model, image_model: imageModel }).then((r) => r.data);
export const adminRemoveBYOKKey = (provider) => client.delete(`/admin/ai/byok/${provider}`).then((r) => r.data);
export const adminSetActiveBYOKProvider = (provider) => client.post('/admin/ai/byok/active', { provider }).then((r) => r.data);
// ---------------- Perplexity research key (Gear Library + Blog Writer grounding) ----------------
export const adminGetResearchKeyStatus = () => client.get('/admin/ai/research-key').then((r) => r.data);
export const adminSaveResearchKey = (apiKey, model) => client.put('/admin/ai/research-key', { api_key: apiKey, model }).then((r) => r.data);
export const adminRemoveResearchKey = () => client.delete('/admin/ai/research-key').then((r) => r.data);
export const createBlueprint = (payload) => client.post('/intelligence/blueprints', payload).then((r) => r.data);
export const getBlueprint = (id) => client.get(`/intelligence/blueprints/${id}`).then((r) => r.data);

// ---------------- Admin: Intelligence + Leads ----------------
export const adminListBlueprints = (status) =>
  client.get('/admin/blueprints', { params: status ? { status } : {} }).then((r) => r.data);
export const adminGetBlueprint = (id) => client.get(`/admin/blueprints/${id}`).then((r) => r.data);
export const adminUpdateBlueprint = (id, payload) => client.patch(`/admin/blueprints/${id}`, payload).then((r) => r.data);
export const adminRegenerateBlueprint = (id) => client.post(`/admin/blueprints/${id}/regenerate`).then((r) => r.data);
export const adminDeleteBlueprint = (id) => client.delete(`/admin/blueprints/${id}`).then((r) => r.data);
export const adminListLeads = (params = {}) => client.get('/admin/leads', { params }).then((r) => r.data);
export const adminUpdateLead = (id, payload) => client.patch(`/admin/leads/${id}`, payload).then((r) => r.data);
export const adminDeleteLead = (id) => client.delete(`/admin/leads/${id}`).then((r) => r.data);

export const adminRegenerateBlueprintSection = (id, section) =>
  client.post(`/admin/blueprints/${id}/regenerate`, { section }).then((r) => r.data);
export const adminListBlueprintSections = () => client.get('/admin/blueprint-sections').then((r) => r.data);
export const adminResendBlueprintEmail = (id) => client.post(`/admin/blueprints/${id}/email/resend`).then((r) => r.data);

// ---------------- Shoot Mode ----------------
export const adminGetShoot = (id) => client.get(`/admin/blueprints/${id}/shoot`).then((r) => r.data);
export const adminUpdateShoot = (id, payload) => client.patch(`/admin/blueprints/${id}/shoot`, payload).then((r) => r.data);
export const adminCreateShootShare = (id) => client.post(`/admin/blueprints/${id}/shoot/share`).then((r) => r.data);
export const adminRevokeShootShare = (id) => client.delete(`/admin/blueprints/${id}/shoot/share`).then((r) => r.data);
export const sharedGetShoot = (token) => client.get(`/shoot/${token}`).then((r) => r.data);
export const sharedUpdateShoot = (token, payload) => client.patch(`/shoot/${token}`, payload).then((r) => r.data);

// ---------------- Client gallery: favorites + downloads ----------------
export const getMyFavorites = (gallerySlug) =>
  client.get('/client/favorites', { params: gallerySlug ? { gallery_slug: gallerySlug } : {} }).then((r) => r.data);
export const toggleFavorite = (gallerySlug, imageId) =>
  client.post('/client/favorites/toggle', { gallery_slug: gallerySlug, image_id: imageId }).then((r) => r.data);
export const downloadImage = async (gallerySlug, imageId, resolution = 'original') => {
  const res = await client.get(`/client/galleries/${gallerySlug}/images/${imageId}/download`, {
    params: { resolution },
    responseType: 'blob',
    timeout: 60000,
  });
  const disposition = res.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: res.data, filename: match ? match[1] : `LXRA-${gallerySlug}.jpg` };
};
export const downloadGalleryZip = async (gallerySlug, scope = 'all', resolution = 'original') => {
  const res = await client.get(`/client/galleries/${gallerySlug}/download-zip`, {
    params: { scope, resolution },
    responseType: 'blob',
    timeout: 120000,
  });
  const disposition = res.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: res.data, filename: match ? match[1] : `LXRA-${gallerySlug}.zip` };
};
export const adminGetClientFavorites = (clientId) => client.get(`/admin/clients/${clientId}/favorites`).then((r) => r.data);
export const adminFavoritesSummary = () => client.get('/admin/favorites/summary').then((r) => r.data);

// ---------------- LXRA Gallery Phase 2 (Wave A): uploads, selections, comments ----------------
export const adminUploadGalleryImages = (slug, files, onProgress) => {
  const formData = new FormData();
  Array.from(files).forEach((f) => formData.append('files', f));
  return client
    .post(`/admin/galleries/${slug}/images/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
      },
    })
    .then((r) => r.data);
};
export const adminNotifyGalleryReady = (slug) => client.post(`/admin/galleries/${slug}/notify`).then((r) => r.data);

export const getMySelections = (gallerySlug) =>
  client.get('/client/selections', { params: gallerySlug ? { gallery_slug: gallerySlug } : {} }).then((r) => r.data);
export const toggleSelection = (gallerySlug, imageId) =>
  client.post('/client/selections/toggle', { gallery_slug: gallerySlug, image_id: imageId }).then((r) => r.data);

export const getGalleryComments = (slug) => client.get(`/client/galleries/${slug}/comments`).then((r) => r.data);
export const addGalleryComment = (slug, imageId, body, isRevisionRequest) =>
  client
    .post(`/client/galleries/${slug}/images/${imageId}/comments`, { body, is_revision_request: isRevisionRequest })
    .then((r) => r.data);

export const adminGetGallerySelections = (slug) => client.get(`/admin/galleries/${slug}/selections`).then((r) => r.data);
export const adminGetGalleryComments = (slug) => client.get(`/admin/galleries/${slug}/comments`).then((r) => r.data);
export const adminResolveComment = (id, resolved) => client.patch(`/admin/comments/${id}`, { resolved }).then((r) => r.data);
export const adminReplyComment = (id, reply, notifyClient = true) =>
  client.post(`/admin/comments/${id}/reply`, { reply, notify_client: notifyClient }).then((r) => r.data);
export const adminDeleteComment = (id) => client.delete(`/admin/comments/${id}`).then((r) => r.data);

// ---------------- LXRA Gallery Phase 2 (Wave B): share links, before/after ----------------
export const adminGetShareLink = (slug) => client.get(`/admin/galleries/${slug}/share`).then((r) => r.data);
export const adminCreateShareLink = (slug, expiresInHours) =>
  client.post(`/admin/galleries/${slug}/share`, { expires_in_hours: expiresInHours }).then((r) => r.data);
export const adminRevokeShareLink = (slug) => client.delete(`/admin/galleries/${slug}/share`).then((r) => r.data);

export const getSharedGallery = (token) => client.get(`/share/${token}`).then((r) => r.data);
export const downloadSharedImage = async (token, imageId, resolution = 'original') => {
  const res = await client.get(`/share/${token}/images/${imageId}/download`, {
    params: { resolution },
    responseType: 'blob',
    timeout: 60000,
  });
  const disposition = res.headers['content-disposition'] || '';
  const match = /filename="?([^"]+)"?/.exec(disposition);
  return { blob: res.data, filename: match ? match[1] : `LXRA-photo.jpg` };
};

export const adminListBeforeAfterPairs = (slug) => client.get(`/admin/galleries/${slug}/before-after`).then((r) => r.data);
export const adminCreateBeforeAfterPair = (slug, beforeImageId, afterImageId, caption) =>
  client.post(`/admin/galleries/${slug}/before-after`, { before_image_id: beforeImageId, after_image_id: afterImageId, caption }).then((r) => r.data);
export const adminDeleteBeforeAfterPair = (id) => client.delete(`/admin/before-after/${id}`).then((r) => r.data);

export default client;

// ---------------- LXRA Creator (Phase 3): packages, bookings, payments, analytics ----------------
export const getPublicPackages = (serviceSlug, studioHandle) =>
  client.get('/packages', { params: { ...(serviceSlug ? { service_slug: serviceSlug } : {}), ...(studioHandle ? { studio: studioHandle } : {}) } }).then((r) => r.data);
export const requestBooking = (payload) => client.post('/bookings/request', payload).then((r) => r.data);
export const getBookingAvailability = (studioHandle, months = 6) =>
  client.get('/bookings/availability', { params: { ...(studioHandle ? { studio: studioHandle } : {}), months } }).then((r) => r.data);
export const createInstantBooking = (payload) => client.post('/bookings', payload).then((r) => r.data);
export const getMyBookings = () => client.get('/client/bookings').then((r) => r.data);

export const adminListPackages = () => client.get('/admin/packages').then((r) => r.data);
export const adminCreatePackage = (payload) => client.post('/admin/packages', payload).then((r) => r.data);
export const adminUpdatePackage = (id, payload) => client.patch(`/admin/packages/${id}`, payload).then((r) => r.data);
export const adminDeletePackage = (id) => client.delete(`/admin/packages/${id}`).then((r) => r.data);

export const adminListBookings = (params = {}) => client.get('/admin/bookings', { params }).then((r) => r.data);
export const adminGetBooking = (id) => client.get(`/admin/bookings/${id}`).then((r) => r.data);
export const adminCreateBooking = (payload) => client.post('/admin/bookings', payload).then((r) => r.data);
export const adminUpdateBooking = (id, payload) => client.patch(`/admin/bookings/${id}`, payload).then((r) => r.data);
export const adminDeleteBooking = (id) => client.delete(`/admin/bookings/${id}`).then((r) => r.data);
export const adminAddPayment = (bookingId, payload) => client.post(`/admin/bookings/${bookingId}/payments`, payload).then((r) => r.data);
export const adminDeletePayment = (bookingId, paymentId) => client.delete(`/admin/bookings/${bookingId}/payments/${paymentId}`).then((r) => r.data);

export const adminGetAnalytics = (months = 6) => client.get('/admin/analytics', { params: { months } }).then((r) => r.data);

// ---------------- LXRA Location Scout (Phase 4.0) ----------------
export const mediaUrl = (path) => (path ? `${API}/media/${path}` : null);
export const adminScoutConfig = () => client.get('/admin/scout/config').then((r) => r.data);
export const adminScoutPreview = (payload) => client.post('/admin/scout/preview', payload).then((r) => r.data);
export const adminCreateScout = (payload) => client.post('/admin/scout', payload).then((r) => r.data);
export const adminScoutFromBlueprint = (blueprintId) => client.post(`/admin/blueprints/${blueprintId}/scout`).then((r) => r.data);
export const adminListScouts = (params = {}) => client.get('/admin/scout', { params }).then((r) => r.data);
export const adminGetScout = (id) => client.get(`/admin/scout/${id}`).then((r) => r.data);
export const adminUpdateScout = (id, payload) => client.patch(`/admin/scout/${id}`, payload).then((r) => r.data);
export const adminDeleteScout = (id) => client.delete(`/admin/scout/${id}`).then((r) => r.data);
export const adminRegenerateScout = (id) => client.post(`/admin/scout/${id}/regenerate`).then((r) => r.data);
export const adminRegenerateScoutImage = (id) => client.post(`/admin/scout/${id}/regenerate-image`).then((r) => r.data);
export const adminScoutReferencePhotos = (id) => client.get(`/admin/scout/${id}/reference-photos`).then((r) => r.data);
export const adminScoutPhotoUrl = (name, maxWidth = 1400) =>
  `${API}/admin/scout/places/photo?name=${encodeURIComponent(name)}&max_width=${maxWidth}`;
export const adminGetMyGear = () => client.get('/admin/me/gear').then((r) => r.data);
export const adminUpdateMyGear = (payload) => client.patch('/admin/me/gear', payload).then((r) => r.data);
// Shoot Simulator (Phase 4.1)
export const adminSimulateScout = (id, payload) => client.post(`/admin/scout/${id}/simulate`, payload).then((r) => r.data);
export const adminRerenderSimFrame = (id, idx, kind = 'frames') =>
  client.post(`/admin/scout/${id}/simulate/frames/${idx}/rerender`, null, { params: { kind } }).then((r) => r.data);
export const adminDeleteSimulation = (id) => client.delete(`/admin/scout/${id}/simulation`).then((r) => r.data);
// Phase 4.2: site map, live sun, custom moments
export const adminRefreshScoutSite = (id) => client.post(`/admin/scout/${id}/site/refresh`).then((r) => r.data);
export const adminScoutSunNow = (id) => client.get(`/admin/scout/${id}/sun-now`).then((r) => r.data);
export const adminEditSimFrame = (id, idx, payload) => client.patch(`/admin/scout/${id}/simulation/frames/${idx}`, payload).then((r) => r.data);
export const adminAddSimFrame = (id, payload) => client.post(`/admin/scout/${id}/simulation/frames`, payload).then((r) => r.data);
export const adminDeleteSimFrame = (id, idx) => client.delete(`/admin/scout/${id}/simulation/frames/${idx}`).then((r) => r.data);
export const adminReorderSimFrames = (id, order) => client.post(`/admin/scout/${id}/simulation/frames/reorder`, { order }).then((r) => r.data);
// Phase 4.3: custom landmarks + public shoot live sun
export const adminAddScoutMarker = (id, payload) => client.post(`/admin/scout/${id}/markers`, payload).then((r) => r.data);
export const adminUpdateScoutMarker = (id, markerId, payload) => client.patch(`/admin/scout/${id}/markers/${markerId}`, payload).then((r) => r.data);
export const adminDeleteScoutMarker = (id, markerId) => client.delete(`/admin/scout/${id}/markers/${markerId}`).then((r) => r.data);
export const sharedShootSunNow = (token) => client.get(`/shoot/${token}/sun-now`).then((r) => r.data);
// Phase 4.4: AI Culling Assist
export const adminCullEstimate = (slug) => client.get(`/admin/galleries/${slug}/cull/estimate`).then((r) => r.data);
export const adminGetCulling = (slug) => client.get(`/admin/galleries/${slug}/cull`).then((r) => r.data);
export const adminStartCulling = (slug, payload) => client.post(`/admin/galleries/${slug}/cull`, payload).then((r) => r.data);
export const adminClearCulling = (slug) => client.delete(`/admin/galleries/${slug}/cull`).then((r) => r.data);
export const adminSetImageVisibility = (slug, payload) => client.post(`/admin/galleries/${slug}/images/visibility`, payload).then((r) => r.data);
export const adminBulkDeleteImages = (slug, payload) => client.post(`/admin/galleries/${slug}/images/bulk-delete`, payload).then((r) => r.data);
// Phase 4.5: AI Pose Assistant
export const adminGenerateFramePoses = (id, idx) => client.post(`/admin/scout/${id}/simulation/frames/${idx}/poses`, {}, { timeout: 90000 }).then((r) => r.data);
export const adminClearFramePoses = (id, idx) => client.delete(`/admin/scout/${id}/simulation/frames/${idx}/poses`).then((r) => r.data);
// Phase 4.6: LXRA Color Intelligence
export const adminColorOptions = () => client.get('/admin/color/options').then((r) => r.data);
export const adminColorProfiles = () => client.get('/admin/color/profiles').then((r) => r.data);
export const adminColorCreateProfile = (payload) => client.post('/admin/color/profiles', payload).then((r) => r.data);
export const adminColorUpdateProfile = (id, payload) => client.patch(`/admin/color/profiles/${id}`, payload).then((r) => r.data);
export const adminColorDeleteProfile = (id) => client.delete(`/admin/color/profiles/${id}`).then((r) => r.data);
export const adminColorProject = (slug) => client.get(`/admin/color/projects/${slug}`).then((r) => r.data);
export const adminColorUpdateProject = (slug, payload) => client.put(`/admin/color/projects/${slug}`, payload).then((r) => r.data);
export const adminColorAnalyze = (slug, payload) => client.post(`/admin/color/projects/${slug}/analyze`, payload || {}).then((r) => r.data);
export const adminColorClearAnalysis = (slug) => client.delete(`/admin/color/projects/${slug}/analysis`).then((r) => r.data);
export const adminColorSetDecisions = (slug, imageId, payload) => client.patch(`/admin/color/projects/${slug}/images/${imageId}/decisions`, payload).then((r) => r.data);
export const adminColorBulkDecisions = (slug, payload) => client.post(`/admin/color/projects/${slug}/decisions/bulk`, payload).then((r) => r.data);
// Phase 4.6 CI-2/CI-3 + Style Library
export const adminColorEstimate = (slug) => client.get(`/admin/color/projects/${slug}/estimate`).then((r) => r.data);
export const adminColorConfirmScenes = (slug, payload) => client.post(`/admin/color/projects/${slug}/confirm-scenes`, payload || {}).then((r) => r.data);
export const adminColorPreview = (slug, imageId, payload) => client.post(`/admin/color/projects/${slug}/images/${imageId}/preview`, payload || {}, { timeout: 60000 }).then((r) => r.data);
export const adminColorApply = (slug, imageId) => client.post(`/admin/color/projects/${slug}/images/${imageId}/apply`, {}, { timeout: 90000 }).then((r) => r.data);
export const adminColorRevert = (slug, imageId) => client.post(`/admin/color/projects/${slug}/images/${imageId}/revert`).then((r) => r.data);
export const adminColorApplyApproved = (slug, payload) => client.post(`/admin/color/projects/${slug}/apply-approved`, payload || {}).then((r) => r.data);
export const adminColorRevertAll = (slug) => client.post(`/admin/color/projects/${slug}/revert-all`).then((r) => r.data);
export const adminColorDefaultProfile = () => client.get('/admin/color/profiles/default').then((r) => r.data);
export const adminColorSetDefaultProfile = (profileId) => client.put('/admin/color/profiles/default', { profile_id: profileId }).then((r) => r.data);
export const adminColorResetProfile = (id) => client.post(`/admin/color/profiles/${id}/reset`).then((r) => r.data);
export const adminColorProfilesUsage = () => client.get('/admin/color/profiles/usage').then((r) => r.data);

/**
 * Turn any API error into a string that is safe to render. FastAPI validation
 * errors may arrive as a list of objects — rendering those directly crashes React.
 */
export const apiErrorMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  if (!err) return fallback;
  if (!err.response) return err.code === 'ECONNABORTED' ? 'The request timed out. Please try again.' : 'Network error — please check your connection and try again.';
  const detail = err.response?.data?.detail ?? err.response?.data?.message;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => (typeof d === 'string' ? d : d?.msg ? `${(d.loc || []).filter((l) => l !== 'body').join('.') || 'Input'}: ${d.msg}` : null)).filter(Boolean);
    if (msgs.length) return msgs.join(' ');
  }
  if (detail && typeof detail === 'object' && typeof detail.msg === 'string') return detail.msg;
  if (err.response.status === 429) return 'Too many attempts — please wait a moment and try again.';
  if (err.response.status >= 500) return 'The server had a problem. Please try again shortly.';
  return fallback;
};

// ---------------- LXRA Marketplace (Phase 5.1) ----------------
export const getMarketplaceStyles = () => client.get('/marketplace/styles').then((r) => r.data);
export const searchPhotographers = (params = {}) =>
  client.get('/photographers', { params, paramsSerializer: { indexes: null }, timeout: 30000 }).then((r) => r.data);
export const getPhotographerProfile = (handle) => client.get(`/photographers/${handle}`).then((r) => r.data);

export const studioGetProfile = () => client.get('/studio/profile').then((r) => r.data);
export const studioUpdateProfile = (payload) => client.put('/studio/profile', payload, { timeout: 30000 }).then((r) => r.data);
export const studioUploadAsset = (kind, file) => {
  const fd = new FormData();
  fd.append('file', file);
  return client.post('/studio/profile/upload', fd, { params: { kind }, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }).then((r) => r.data);
};
export const studioPortfolioCandidates = () => client.get('/studio/profile/portfolio-candidates').then((r) => r.data);

export const ownerListStudios = (status) => client.get('/admin/studios', { params: status ? { status } : {} }).then((r) => r.data);
export const ownerGetStudio = (id) => client.get(`/admin/studios/${id}`).then((r) => r.data);
export const ownerModerateStudio = (id, payload) => client.patch(`/admin/studios/${id}`, payload).then((r) => r.data);
export const ownerDeleteStudio = (id) => client.delete(`/admin/studios/${id}`).then((r) => r.data);

// ---------------- LXRA Marketplace: Availability calendar (Phase 5.2) ----------------
export const studioGetAvailability = () => client.get('/studio/availability').then((r) => r.data);
export const studioUpdateAvailability = (blockedDates) => client.put('/studio/availability', { blocked_dates: blockedDates }).then((r) => r.data);

// ---------------- LXRA Marketplace: Reviews & Ratings (Phase 5.3) ----------------
export const getPhotographerReviews = (handle) => client.get(`/photographers/${handle}/reviews`).then((r) => r.data);
export const getReviewEligibility = (bookingId, token) => client.get('/reviews/eligibility', { params: { booking_id: bookingId, token } }).then((r) => r.data);
export const submitReview = (payload) => client.post('/reviews', payload).then((r) => r.data);
export const studioGetReviews = () => client.get('/studio/reviews').then((r) => r.data);
export const ownerListReviews = (studioId) => client.get('/admin/reviews', { params: studioId ? { studio_id: studioId } : {} }).then((r) => r.data);
export const ownerModerateReview = (id, payload) => client.patch(`/admin/reviews/${id}`, payload).then((r) => r.data);

// ---------------- LXRA Marketplace: Payouts & Revenue (Phase 5.5, simulated) ----------------
export const studioGetEarnings = () => client.get('/studio/earnings').then((r) => r.data);
export const studioGetPayoutMethod = () => client.get('/studio/payout-method').then((r) => r.data);
export const studioUpdatePayoutMethod = (payload) => client.put('/studio/payout-method', payload).then((r) => r.data);
export const studioRequestPayout = () => client.post('/studio/payouts').then((r) => r.data);
export const ownerGetRevenue = () => client.get('/admin/revenue').then((r) => r.data);
